<script setup lang="ts">
import type {
  ProductAccessoryCategory,
  Currency,
  Price,
  ProductAccessoryPrice,
} from '~/types/api'

const props = defineProps<{
  setNid?: number
}>()

const accessories = ref({})

// TODO: Lazy async data with skeleton

const accessoriesRepository = createAccessoriesRepository()

const { data: categories } = await useCachedAsyncData(
  'accessory-categories',
  () => accessoriesRepository.getAllAccessoryCategories()
)

const promises =
  categories.value?.items.map((category) => {
    const filter = [
      {
        [APIFilterOP.EQUALS]: {
          'productAccessoryCategory.nid': category.nid,
        },
      },
    ]

    return useAsyncData(props.setNid + '_' + category.nid, () =>
      accessoriesRepository.getAllAccessoriesBySet(props.setNid, {
        filter: APIFilters.makeFilter(filter),
      })
    )
  }) || []

const values = await Promise.allSettled(promises)

categories.value?.items.map((category, index) => {
  const val = values[index]

  if (!isFulfilled(val)) {
    return
  }

  accessories.value[category.nid || 0] = val.value?.data.value?.items
})

const selectedCategory = ref<null | ProductAccessoryCategory>(null)

const filteredCategories = computed(() => {
  if (!categories.value) {
    return []
  }

  return categories.value.items.filter(
    (category) => accessories.value[category.nid]?.length > 0
  )
})

if (categories.value?.items.length) {
  selectedCategory.value = filteredCategories.value[0]
}

const selectedAccessories = computed(() => {
  if (!selectedCategory.value) {
    return []
  } else {
    return accessories.value[selectedCategory.value.nid]
  }
})

/// /////////// PRICES ///////////////////

const userStore = useUser()
const { isLoggedIn, userCustomerGroupId } = storeToRefs(userStore)
const userConfigStore = useUserConfig()
const { customerGroupId, priceCategoryId } = storeToRefs(userConfigStore)

const pricesLoading = ref(false)
const currency = ref<Currency | null>(null)
type AccessoryPrices = {
  [accessoryId: string]: Price
}
const accessoryPrices = ref<AccessoryPrices>({})

const productSetsRepository = createProductSetsRepository()
const customerGroupsRepository = createCustomerGroupsRepository()

watch(
  [isLoggedIn, userCustomerGroupId, customerGroupId, priceCategoryId],
  async () => {
    if (!isLoggedIn.value || !userCustomerGroupId.value) {
      return
    }

    pricesLoading.value = true
    accessoryPrices.value = {}

    const { data: pricesPromise } = await useAsyncData(
      'accessories_prices_' + props.setNid,
      () =>
        productSetsRepository.getAllAccessoryPrices(
          props.setNid,
          customerGroupId.value ?? userCustomerGroupId.value,
          priceCategoryId.value ?? undefined
        )
    )

    const currencyPromise =
      customerGroupsRepository.getCurrencyForCustomerGroup(
        customerGroupId.value ?? userCustomerGroupId.value
      )

    const values = await Promise.allSettled([pricesPromise, currencyPromise])
    ;(pricesPromise.value || []).forEach(
      (accessoryPrice: ProductAccessoryPrice) => {
        if (accessoryPrice.prices?.length) {
          accessoryPrices.value[accessoryPrice.nid] = accessoryPrice.prices?.[0]
        }
      }
    )

    const currencyValue = values[1]

    if (currencyValue.status === 'fulfilled') {
      currency.value = currencyValue.value
    }

    pricesLoading.value = false
  },
  { immediate: true }
)
</script>

<template>
  <UiSection
    v-if="filteredCategories.length"
    gray
    :title="$t('base.products.accessories.title')"
  >
    <ProductSurfacesCarousel class="border-b-0">
      <UiCarouselItem
        v-for="category in filteredCategories"
        :key="category.nid"
        class="basis-auto"
      >
        <button
          class="flex w-fit flex-col items-center whitespace-nowrap px-5 py-3 hover:text-neutral-400"
          :class="{
            'bg-white': category.nid === selectedCategory?.nid,
          }"
          @click="selectedCategory = category"
        >
          <div class="text-xl font-bold">
            {{ category.title }}
          </div>
        </button>
      </UiCarouselItem>
    </ProductSurfacesCarousel>
    <div class="h-1 bg-white" />
    <div class="flex flex-wrap">
      <ProductAccessoryCard
        v-for="accessory in selectedAccessories"
        :key="accessory.nid"
        class="w-full pr-0 md:w-1/3 md:pr-2 lg:w-1/6"
        :accessory="accessory"
        :price="accessoryPrices[accessory.nid]"
        :currency="currency"
        :price-loading="pricesLoading"
      />
    </div>
  </UiSection>
</template>
